import { autorun, makeAutoObservable, runInAction } from "mobx";
import type { Socket } from "socket.io-client";
import type RootStore from "stores/RootStore";
import {
  NOTIFICATION_REALTIME_SERVICES,
  acquireRealtimeSocket,
  notificationCrudChannel,
  peekRealtimeSocket,
  realtimeDiagnostics,
  subscribeRealtime,
  type RealtimeLease,
} from "realtime";

/**
 * RealtimeStore
 *
 * Observable façade over the shared realtime bus (`src/realtime/`) for the
 * `notifications` service: it records the most recent CRUD event as
 * {@link RealtimeStore.lastEvent} and mirrors the shared connection's state.
 * `ApiNotificationStore` reacts to `lastEvent` as a backstop for the bell.
 *
 * IT NO LONGER OWNS A SOCKET. This store used to open its own JWT-authenticated
 * Socket.io connection — one of seven independent connection owners in the app,
 * each with its own copy of the handshake, refcount and teardown. `connect()`
 * now takes a lease on the bus's single connection (normally the raw socket
 * under the Feathers client, which is already open and authenticated), so the
 * store costs ZERO additional websockets. `disconnect()` releases the lease; it
 * never disconnects a socket the bus does not own.
 *
 * Auth is the bus's business: `connect()` takes no token and cannot be called
 * "without auth" — if no connection can be established that is recorded on
 * `realtimeDiagnostics` and console.errored, never silently swallowed.
 */

/**
 * Services this store surfaces as `lastEvent`. Must match the backend
 * `realTimeServices` set registered in `src/channels.ts` on the API; the
 * canonical list lives in `realtime/channels.ts`.
 */
const REALTIME_SERVICES = NOTIFICATION_REALTIME_SERVICES;

class RealtimeStore {
  rootStore: RootStore;
  /** The shared socket, exposed for back-compat. NOT owned by this store. */
  socket: Socket | null = null;
  connected = false;
  /**
   * Count of realtime problems the bus has recorded (failed handshakes,
   * unavailable connections). Historically this counted `connect_error`s on
   * this store's own socket; the bus now records those centrally.
   */
  reconnectAttempts = 0;
  lastEvent: {
    service: string;
    action: string;
    id?: number | string;
    at: number;
  } | null = null;

  private lease: RealtimeLease | null = null;
  private unsubscribe: (() => void) | null = null;
  private stateDisposer: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false, socket: false });
  }

  /**
   * Join the shared realtime connection and start recording `notifications`
   * events. Idempotent — repeated calls are no-ops while already subscribed.
   */
  async connect(): Promise<void> {
    if (this.unsubscribe) return;

    this.unsubscribe = subscribeRealtime(
      this.rootStore,
      notificationCrudChannel,
      (event) => {
        const id = (event.record as { id?: number | string } | null)?.id;
        runInAction(() => {
          this.lastEvent = {
            service: event.service,
            action: event.action,
            id,
            at: Date.now(),
          };
        });
      }
    );

    // Mirror the bus's observable connection state onto this store, so
    // `connected` stays honest across drops and reconnects without this store
    // touching the socket lifecycle itself.
    this.stateDisposer = autorun(() => {
      const connected = realtimeDiagnostics.connected;
      const problems = realtimeDiagnostics.problems.length;
      const socket = peekRealtimeSocket(this.rootStore);
      runInAction(() => {
        this.socket = socket;
        this.connected = connected;
        this.reconnectAttempts = problems;
      });
    });

    // A lease of our own keeps the connection alive for `socket` consumers even
    // when the notifications channel itself has no other subscriber.
    this.lease = await acquireRealtimeSocket(this.rootStore, "realtime-store");
  }

  /** Leave the shared connection. Never disconnects a socket the bus owns. */
  disconnect(): void {
    this.stateDisposer?.();
    this.stateDisposer = null;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.lease?.release();
    this.lease = null;
    runInAction(() => {
      this.socket = null;
      this.connected = false;
    });
  }

  /** Store teardown — same as {@link RealtimeStore.disconnect}. */
  dispose(): void {
    this.disconnect();
  }

  /**
   * Called by RootStore.purgeUserData on sign-out: drops the last event seen.
   * The connection itself is plumbing, not the person's, and the socket layer
   * re-authenticates it for whoever signs in next — so it is left up.
   */
  reset(): void {
    this.lastEvent = null;
  }
}

export default RealtimeStore;
export { REALTIME_SERVICES };
